<template>
  <form
      @submit.prevent="saveSchedule"
      @keydown.enter="onEnterKey"
      autocomplete="off"
      class="w-full max-h-screen bg-background flex flex-col relative min-w-0"
      :class="{ 'overflow-y-hidden': isBusy }"
  >
    <div
        v-show="isBusy"
        class="absolute inset-0 bg-faint-white flex items-center justify-center z-50"
    >
      <spinner :color="'text-black'" :size="10"/>
    </div>
    <div
        v-if="stuckAssignments && stuckAssignments.length"
        class="mx-6 mt-4 rounded border border-orange-300 bg-orange-50 p-4"
        role="alert"
    >
      <div class="flex items-start justify-between">
        <div>
          <p class="text-sm font-semibold text-orange-900">
            Some engineer days fall outside the current schedule range.
          </p>
          <p class="mt-1 text-xs text-orange-900">
            Either widen the schedule's start/end date to cover them, or remove the engineer day(s) below before saving.
          </p>
          <ul class="mt-2 space-y-1">
            <li
                v-for="(s, i) in stuckAssignments"
                :key="'stuck-' + i"
                class="text-sm text-orange-900 flex items-center gap-2"
            >
              <span class="font-semibold">{{ s.engineer_name }}</span>
              <span>on {{ s.date_label }}</span>
              <button
                  type="button"
                  class="text-xs text-red-600 hover:text-red-800 underline"
                  @click="markStuckForRemoval(s.assignment_id)"
              >Remove</button>
            </li>
          </ul>
        </div>
        <button
            type="button"
            class="ml-4 text-orange-900 hover:text-orange-700"
            aria-label="Dismiss"
            @click="dismissStuckAssignmentsBanner"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
    <div
        v-if="dateChangeBanner"
        class="mx-6 mt-4 rounded border p-4"
        :class="dateChangeBanner.problems.length ? 'border-yellow-300 bg-yellow-50' : 'border-green-300 bg-green-50'"
        role="status"
    >
      <div class="flex items-start justify-between">
        <div>
          <p class="text-sm font-semibold" :class="dateChangeBanner.problems.length ? 'text-yellow-900' : 'text-green-900'">
            Engineer days updated with the new dates.
          </p>
          <ul v-if="dateChangeBanner.lines.length" class="mt-1 space-y-1">
            <li v-for="(line, i) in dateChangeBanner.lines" :key="'dc-' + i" class="text-sm" :class="dateChangeBanner.problems.length ? 'text-yellow-900' : 'text-green-900'">
              {{ line }}
            </li>
          </ul>
          <ul v-if="dateChangeBanner.problems.length" class="mt-2 space-y-1">
            <li v-for="(line, i) in dateChangeBanner.problems" :key="'dcp-' + i" class="text-sm text-red-700">
              {{ line }}
            </li>
          </ul>
        </div>
        <button
            type="button"
            class="ml-4"
            :class="dateChangeBanner.problems.length ? 'text-yellow-900 hover:text-yellow-700' : 'text-green-900 hover:text-green-700'"
            aria-label="Dismiss"
            @click="dismissDateChangeBanner"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
    <div
        v-if="saveErrorBanner"
        class="mx-6 mt-4 rounded border border-red-300 bg-red-50 p-4"
        role="alert"
    >
      <div class="flex items-start justify-between">
        <div>
          <p class="text-sm font-semibold text-red-900">
            {{ saveErrorBanner.title }}
          </p>
          <p
              v-if="saveErrorBanner.out_of_range && saveErrorBanner.out_of_range.length"
              class="mt-1 text-xs text-red-900"
          >
            Out-of-range engineer days:
          </p>
          <ul
              v-if="saveErrorBanner.out_of_range && saveErrorBanner.out_of_range.length"
              class="mt-1 space-y-1"
          >
            <li
                v-for="(item, i) in saveErrorBanner.out_of_range"
                :key="'oor-' + i"
                class="text-sm text-red-700"
            >
              <span class="font-semibold">{{ item.engineer_name }}</span>
              <span> on </span>
              <span>{{ item.date_label }}</span>
            </li>
          </ul>
          <p
              v-if="saveErrorBanner.clashes && saveErrorBanner.clashes.length"
              class="mt-1 text-xs text-red-900"
          >
            Clashing engineer bookings:
          </p>
          <ul
              v-if="saveErrorBanner.clashes && saveErrorBanner.clashes.length"
              class="mt-1 space-y-1"
          >
            <li
                v-for="(item, i) in saveErrorBanner.clashes"
                :key="'clash-' + i"
                class="text-sm text-red-700"
            >
              <span class="font-semibold">{{ item.engineer_name }}</span>
              <span v-if="item.date_label"> on {{ item.date_label }}</span>
              <span> — {{ item.detail }}</span>
            </li>
          </ul>
          <table
              v-if="saveErrorBanner.equipment_conflicts && saveErrorBanner.equipment_conflicts.length"
              class="mt-2 text-sm text-red-900 border border-red-300"
          >
            <thead>
              <tr class="bg-red-100 text-left">
                <th class="px-3 py-1 font-semibold">Serial Number</th>
                <th class="px-3 py-1 font-semibold">Product Code</th>
                <th class="px-3 py-1 font-semibold">Job No</th>
                <th class="px-3 py-1 font-semibold">Booked For</th>
              </tr>
            </thead>
            <tbody>
              <tr
                  v-for="(item, i) in saveErrorBanner.equipment_conflicts"
                  :key="'eqc-' + i"
                  class="border-t border-red-200"
              >
                <td class="px-3 py-1">{{ item.serial_number || item.equipment_id }}</td>
                <td class="px-3 py-1">{{ item.product_code || 'N/A' }}</td>
                <td class="px-3 py-1 font-semibold">{{ item.job_id }}</td>
                <td class="px-3 py-1 capitalize">{{ item.category }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
            type="button"
            class="ml-4 text-red-900 hover:text-red-700"
            aria-label="Dismiss"
            @click="dismissSaveErrorBanner"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
    <div
        v-if="showAssignmentResultBanner"
        class="mx-6 mt-4 rounded border border-yellow-300 bg-yellow-50 p-4"
        role="alert"
    >
      <div class="flex items-start justify-between">
        <div>
          <p class="text-sm font-semibold text-yellow-900">
            {{ assignmentResult.created ? assignmentResult.created.length : 0 }} engineer assignment(s) saved.
            <span class="text-red-700">
              {{ assignmentResult.rejected.length }} could not be booked.
            </span>
          </p>
          <p class="mt-1 text-xs text-yellow-900">
            The schedule itself is saved and is visible in the Scheduled tab. Reschedule the date, adjust the times, or assign a different engineer to resolve.
          </p>
          <ul class="mt-2 space-y-1">
            <li
                v-for="(r, i) in assignmentResult.rejected"
                :key="i"
                class="text-sm text-red-700"
            >
              <span class="font-semibold">{{ r.engineer_name || r.engineer_id }}</span>
              <span> on </span>
              <span>{{ formatEpoch(r.scheduled_date) }}</span>
              <span> — {{ formatRejectionReason(r) }}</span>
            </li>
          </ul>
        </div>
        <button
            type="button"
            class="ml-4 text-yellow-900 hover:text-yellow-700"
            aria-label="Dismiss"
            @click="dismissAssignmentResult"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
    <page-header :title="pageTitle" :info="getScheduleRef" :enableLogicalBack="true" back-to=" ">
      <button
          v-if="!readOnly"
          @click="saveSchedule"
          type="button"
          class="flex items-center bg-blue-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path
              d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
          />
          <path d="M17 21v-8H7v8M7 3v5h8"/>
        </svg>
        <span>Save</span>
      </button>
    </page-header>

    <main class="pr-6 pl-6 bg-background flex-1 overflow-y-scroll min-w-0 overflow-x-hidden">
      <!-- First Row with Two Responsive Columns -->
      <div class="flex flex-col md:flex-row w-full">
        <!-- Column 1: Customer -->
        <div class="w-full md:w-1/2 bg-white p-5 mt-6 md:mr-3 rounded shadow-md">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Select Customer
            </h3>
            <hr class="flex-1 border-primary ml-4"/>
          </div>
          <div class="flex flex-col md:flex-row gap-8">
            <!-- Left Column -->
            <div class="flex-1 space-y-2">
              <div>
                <label class="uppercase font-semibold text-sm mb-2">Customer</label>
                <ContactAutocomplete class="w-full" :class="{ 'readonly': fieldsLocked }"
                                     v-model="schedule.customer"
                                     @contactSelected="handleContactSelected"
                                     @contactCleared="handleContactCleared" :forceSelection="true" :is_customer="true"
                />
              </div>

              <div v-if="customer.address" class="flex items-center space-x-2">
                <svg class="h-5 w-5 text-orange-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                     stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <p class="text-sm">{{ customer.address }}</p>
              </div>

              <div v-if="customer.people" class="space-y-2">
                <div v-if="customer.people.job_title" class="flex items-center space-x-2">
                  <svg class="h-5 w-5 text-orange-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                       stroke-width="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <p class="text-sm">{{ customer.people.job_title }}</p>
                </div>

                <div class="flex items-center space-x-2" v-if="customer.people.firstname || customer.people.lastname">
                  <svg class="h-5 w-5 text-orange-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                       stroke-width="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <p class="text-sm">{{ customer.people.firstname }} {{ customer.people.lastname }}</p>
                </div>

                <div class="flex items-center space-x-2" v-if="customer.people.email">
                  <svg class="h-5 w-5 text-orange-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                       stroke-width="2">
                    <path
                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                    />
                    <path d="M22 6l-10 7L2 6"/>
                  </svg>
                  <p class="text-sm">{{ customer.people.email }}</p>
                </div>

                <div class="flex items-center space-x-2" v-if="customer.people.mobile || customer.people.telephone">
                  <svg class="h-5 w-5 text-orange-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                       stroke-width="2">
                    <path
                        d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
                    />
                  </svg>
                  <p class="text-sm">{{ customer.people.mobile || customer.people.telephone }}</p>
                </div>

                <div class="flex items-center space-x-2" v-if="customer.vts_number">
                  <span class="font-bold text-orange-700 uppercase">VTS</span>
                  <p class="text-sm">{{ customer.vts_number }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Customer Notes -->
            <div class="flex-1 space-y-2 md:overflow-auto ">
              <div>
                <label class="uppercase font-semibold text-sm mb-2">Customer Notes</label>
                <textarea
                    readonly
                    v-model="customer.comments"
                    class="text-md leading-snug w-full focus:outline-none bge-input pl-2 pr-2 pb-3 pt-2 rounded h-full"
                    placeholder="Notes..."
                    rows="8"
                ></textarea>
              </div>
            </div>
          </div>

        </div>

        <!-- Column 2: Schedule Details -->
        <div class="w-full md:w-1/2 bg-white p-5 mt-6 md:ml-3 rounded shadow-md">
          <div class="flex items-center mb-2">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Set Schedule
            </h3>
            <hr class="flex-1 border-primary ml-4"/>
          </div>

          <!-- Two columns: Left = dates/times + confirmed by, Right = notes -->
          <div class="flex">
            <div class="w-1/2 p-2 flex flex-col">
              <div class="flex space-x-2">
                <div class="flex-1">
                  <label class="block uppercase font-semibold text-sm mb-1">Start Date</label>
                  <Calendar
                      dateFormat="dd/mm/yy"
                      v-model="schedule.start_date" :showIcon="true"
                      :class="{ 'readonly': fieldsLocked }" :disabled="inProgressLock"
                  />
                </div>
                <div class="w-28">
                  <label class="block uppercase font-semibold text-sm mb-1">Time</label>
                  <Calendar
                      v-model="schedule.start_time" :timeOnly="true"
                      :class="{ 'readonly': fieldsLocked }" :disabled="inProgressLock"
                      @blur="onTimeTyped('start_time', $event)"
                  />
                </div>
              </div>
              <div class="flex space-x-2 mt-3">
                <div class="flex-1">
                  <label class="block uppercase font-semibold text-sm mb-1">End Date</label>
                  <Calendar
                      dateFormat="dd/mm/yy"
                      v-model="schedule.end_date" :showIcon="true"
                      :class="{ 'readonly': readOnly }"
                  />
                </div>
                <div class="w-28">
                  <label class="block uppercase font-semibold text-sm mb-1">Time</label>
                  <Calendar
                      v-model="schedule.end_time" :timeOnly="true"
                      :class="{ 'readonly': readOnly }"
                      @blur="onTimeTyped('end_time', $event)"
                  />
                </div>
              </div>
              <div class="flex justify-end mt-2">
                <button
                    v-if="!fieldsLocked"
                    type="button"
                    @click="setAllDayTimes"
                    class="flex items-center bg-orange-200 border hover:bg-orange-300 border-orange-400 hover:border-orange-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-3 py-1 text-sm"
                >
                  All Day (08:30 - 17:00)
                </button>
              </div>
              <div class="mt-3">
                <label class="block uppercase font-semibold text-sm mb-1">Confirmed By</label>
                <input v-model="schedule.confirmed_by" :readonly="fieldsLocked"
                       class="bge-input bge-input-spacing rounded w-full" type="text"/>
              </div>
            </div>

            <div class="w-1/2 p-2 flex flex-col">
              <label class="block uppercase font-semibold text-sm mb-1">Notes</label>
              <textarea
                  :readonly="fieldsLocked"
                  v-model="schedule.notes"
                  class="text-md leading-snug w-full focus:outline-none bge-input bge-input-spacing rounded flex-1"
                  placeholder="Notes..."
              ></textarea>
            </div>
          </div>

          <!-- Flags — single row of compact PrimeVue checkboxes -->
          <div class="flex items-center flex-wrap gap-x-6 gap-y-3 mt-4 px-2">
            <div class="flex items-center gap-2">
              <Checkbox v-model="schedule.is_confirmed" :binary="true" :disabled="fieldsLocked" inputId="cb-confirmed" />
              <label for="cb-confirmed" class="uppercase font-semibold text-sm whitespace-nowrap cursor-pointer">Confirmed</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox v-model="schedule.is_site_visit" :binary="true" :disabled="fieldsLocked" inputId="cb-site-visit" />
              <label for="cb-site-visit" class="uppercase font-semibold text-sm whitespace-nowrap cursor-pointer">Site Visit</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox v-model="schedule.is_pre_pay" :binary="true" :disabled="fieldsLocked" inputId="cb-pre-pay" />
              <label for="cb-pre-pay" class="uppercase font-semibold text-sm whitespace-nowrap cursor-pointer">Pre Pay</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox v-model="schedule.is_install" :binary="true" :disabled="fieldsLocked" inputId="cb-install" />
              <label for="cb-install" class="uppercase font-semibold text-sm whitespace-nowrap cursor-pointer">Install</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox v-model="schedule.requires_parts_collection" :binary="true" :disabled="fieldsLocked" inputId="cb-parts" />
              <label for="cb-parts" class="uppercase font-semibold text-sm whitespace-nowrap cursor-pointer">Collect Parts</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox v-model="schedule.is_contractor" :binary="true" :disabled="fieldsLocked" inputId="cb-contractor" />
              <label for="cb-contractor" class="uppercase font-semibold text-sm whitespace-nowrap cursor-pointer">Contractor</label>
            </div>
          </div>
        </div>

      </div>

      <!-- Engineer Dispatch Board -->
      <div v-if="!schedule.is_contractor" class="w-full bg-white p-5 mt-6 rounded shadow-md min-w-0 overflow-hidden">
        <div class="flex items-center mb-5">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
            Assign Engineers
          </h3>
          <hr class="flex-1 border-primary ml-4"/>
        </div>

        <DispatchBoard
            ref="dispatchBoard"
            v-model="pendingAssignments"
            :startDate="schedule.start_date"
            :endDate="schedule.end_date"
            :defaultStartTime="scheduleStartTimeStr"
            :defaultEndTime="scheduleEndTimeStr"
            :readonly="readOnly"
            :scheduleId="schedule_id"
            @remove-engineer="onEngineerRemoved"
        />

      </div>

      <!-- Contractor Company Picker -->
      <div v-if="schedule.is_contractor" class="w-full bg-white p-5 mt-6 rounded shadow-md">
        <div class="flex items-center mb-5">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
            Assign Contractor
          </h3>
          <hr class="flex-1 border-primary ml-4"/>
        </div>
        <div class="w-full md:w-1/2 lg:w-1/3 space-y-2">
          <label class="uppercase font-semibold text-sm mb-2 block">Contractor</label>
          <ContactAutocomplete class="w-full" :class="{ 'readonly': fieldsLocked }"
                               v-model="schedule.contractor"
                               @contactSelected="handleContractorSelected"
                               @contactCleared="handleContractorCleared"
                               :forceSelection="true" :is_individual="false"
          />
          <p v-if="!schedule.contact_id" class="text-xs text-gray-500">
            Pick a company contact. The engineer dispatch board is hidden while Contractor is on.
          </p>
        </div>
      </div>

      <!-- Equipment Section -->
      <div class="w-full bg-white p-5 mt-6 mb-6 rounded shadow-md">
        <div class="flex items-center mb-5">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
            Select Equipment
          </h3>
          <hr class="flex-1 border-primary ml-4"/>
        </div>

        <EquipmentPlannerList
            :items="plannerItems"
            :visible-items="plannerVisibleItems"
            :filters="plannerFilters"
            :current-schedule-id="currentScheduleId || ''"
            :is-loading="plannerIsLoading"
            :customer-selected="!!(schedule.customer && schedule.customer.contact_id)"
            :bucket-counts="plannerBucketCounts"
            :highlight-equipment-id="highlightEquipmentId"
            :readonly="plannerReadOnly"
            @filter-change="onPlannerFilterChange"
            @toggle-category="onPlannerToggleCategory"
        />
      </div>
    </main>


  </form>
</template>

<script>
import { mapState, mapActions } from "vuex";
import PageHeader from "@/components/PageHeader.vue";
import Panel from "@/components/Panel.vue";
import Spinner from "@/components/Spinner.vue";
import ContactAutocomplete from '@/components/AutoComplete/ContactAutoComplete.vue';
import Icon from '@/components/Icon.vue';
import DispatchBoard from "./Components/DispatchBoard.vue";
import EquipmentPlannerList from "./Components/EquipmentPlannerList.vue";
import notificationMixin from "@/mixins/notificationMixin";
import ScheduleService from "@/services/scheduleService";
import Calendar from "primevue/calendar";
import { isCategoryLockedElsewhere } from "@/utils/equipmentLocks";
import { findPrefillTarget } from "./equipmentPrefill";
import { formatRejectionReason, buildAssignmentResult } from "./assignmentRejection";
import {
  dateToUtcMidnightEpoch,
  epochToLocalDateAtMidnight,
  epochToYyyyMmDd,
  localDateToYyyyMmDd,
  yyyyMmDdToUtcMidnightEpoch,
  isDayInRange,
} from "@/utils/scheduleDate";

const Toggle = () => import("@/components/Toggle.vue");

export default {
  name: "ManageSchedule",
  mixins: [notificationMixin],
  components: {
    PageHeader,
    Panel,
    Spinner,
    ContactAutocomplete,
    DispatchBoard,
    EquipmentPlannerList,
    Icon,
    Calendar,
    Toggle,
  },
  props: {
    schedule_id: String,
    customer_id: String,
    equipment_id: String,
  },
  data() {
    return {
      isBusy: false,
      schedule: {
        customer: null,
        start_date: null,
        end_date: null,
        start_time: null,
        end_time: null,
        notes: '',
        is_confirmed: false,
        confirmed_by: '',
        is_site_visit: false,
        is_pre_pay: false,
        is_install: false,
        requires_parts_collection: false,
        is_contractor: false,
        contact_id: null,
        contractor_name: null,
        contractor: null,
      },
      customer: {},
      readOnly: false,
      pendingAssignments: [],
      initialAssignmentTimes: {},
      selectedEngineer: null,
      assignmentResult: null,
      validationErrors: {},
      highlightEquipmentId: null,
      saveErrorBanner: null,
      dateChangeBanner: null,
      originalScheduleDates: null,
      originalScheduleTimes: null,
      stuckAssignments: [],
    };
  },
  async mounted() {
    await this.enterRouteMode(this.$route);
  },
  async beforeRouteUpdate(to, from, next) {
    await this.enterRouteMode(to);
    next();
  },
  async beforeRouteLeave(to, from, next) {
    next();
  },
  computed: {
    ...mapState('scheduleManage', {
      plannerItems: state => state.equipment,
      plannerFilters: state => state.filters,
      plannerIsLoading: state => state.isLoading,
      currentScheduleId: state => state.schedule_id,
    }),
    plannerVisibleItems() {
      return this.$store.getters['scheduleManage/filteredEquipment'];
    },
    plannerBucketCounts() {
      return this.$store.getters['scheduleManage/bucketCounts'];
    },
    plannerReadOnly() {
      // Mirrors the API: a saved schedule only accepts changes while NotStarted.
      if (!this.schedule_id) return false;
      const status = this.schedule && this.schedule.status;
      return !!status && status !== 'NotStarted';
    },

    // In progress: the end date, end time and engineer ticks stay open so the
    // office can extend or cut a job short and free people up. Everything else
    // on the form is locked, matching what the API will accept.
    inProgressLock() {
      if (!this.schedule_id) return false;
      return !!this.schedule && this.schedule.status === 'InProgress';
    },

    fieldsLocked() {
      return this.readOnly || this.inProgressLock;
    },

    pageTitle() {
      if (this.schedule_id) {
        return "Edit Schedule";
      } else {
        return "Create Schedule";
      }
    },

    getScheduleRef() {
      return this.schedule.job_id ? this.schedule.job_id : "";
    },

    dateRangeDisplay() {
      if (!this.schedule.start_date || !this.schedule.end_date) return "";
      const start = this.$moment(this.schedule.start_date);
      const end = this.$moment(this.schedule.end_date);
      const days = end.diff(start, "days") + 1;
      if (days < 1) return "Invalid range";
      return `${days} day${days !== 1 ? "s" : ""}`;
    },

    scheduleStartTimeStr() {
      if (!this.schedule.start_time) return null;
      return this.$moment(this.schedule.start_time).format('HH:mm');
    },
    scheduleEndTimeStr() {
      if (!this.schedule.end_time) return null;
      return this.$moment(this.schedule.end_time).format('HH:mm');
    },

    activeScheduleId() {
      return this.schedule_id || (this.schedule && this.schedule.schedule_id) || null;
    },
    showAssignmentResultBanner() {
      const r = this.assignmentResult;
      if (!r || !r.rejected || !r.rejected.length) return false;
      const here = this.activeScheduleId;
      return !!here && r.scheduleId === here;
    },

  },

  watch: {
    'schedule.start_date'(newVal, oldVal) {
      if (!newVal) return;
      if (!this.schedule.end_date) {
        this.schedule.end_date = newVal;
        return;
      }
      // Keep the job the same length: the end date moves by the same
      // number of days as the start date.
      const previousStart = oldVal ? localDateToYyyyMmDd(oldVal) : null;
      const nextStart = localDateToYyyyMmDd(newVal);
      const currentEnd = localDateToYyyyMmDd(this.schedule.end_date);
      if (!previousStart || !nextStart || !currentEnd || previousStart === nextStart) return;
      const deltaDays = Math.round(
        (yyyyMmDdToUtcMidnightEpoch(nextStart) - yyyyMmDdToUtcMidnightEpoch(previousStart)) / 86400,
      );
      const shiftedEnd = new Date(this.schedule.end_date);
      shiftedEnd.setDate(shiftedEnd.getDate() + deltaDays);
      this.schedule.end_date = shiftedEnd;
    },
    // schedule-edit and schedule-create are DIFFERENT route records sharing
    // this component, so switching between them reuses the instance without
    // firing mounted or beforeRouteUpdate — the previous schedule's planner
    // state (store schedule_id + selections) leaked into a "new" schedule
    // (field incident: create page opened with another job's 7 items already
    // added). Re-enter route mode on any record swap. Same-record param
    // changes are still handled by beforeRouteUpdate, hence the name guard.
    $route(to, from) {
      if (to.name !== from.name) {
        this.enterRouteMode(to);
      }
    },
  },

  methods: {
    // Typing plain digits into a time field works: 1400 -> 14:00, 0954 -> 09:54,
    // 954 -> 09:54, 9 -> 09:00 (field request). Anything else is left to the
    // Calendar's own parsing.
    onTimeTyped(field, event) {
      if (this.readOnly) return;
      const raw = event && event.target ? String(event.target.value || '').trim() : '';
      if (!raw || raw.includes(':')) return;

      const digits = raw.replace(/\D/g, '');
      if (!digits || digits.length > 4) return;

      let hours;
      let minutes;
      if (digits.length <= 2) {
        hours = parseInt(digits, 10);
        minutes = 0;
      } else {
        hours = parseInt(digits.slice(0, digits.length - 2), 10);
        minutes = parseInt(digits.slice(-2), 10);
      }
      if (isNaN(hours) || isNaN(minutes) || hours > 23 || minutes > 59) return;

      const parsed = new Date();
      parsed.setHours(hours, minutes, 0, 0);
      this.schedule[field] = parsed;
    },

    setAllDayTimes() {
      const start = new Date();
      start.setHours(8, 30, 0, 0);
      const end = new Date();
      end.setHours(17, 0, 0, 0);
      this.schedule.start_time = start;
      this.schedule.end_time = end;
    },

    dismissSaveErrorBanner() {
      this.saveErrorBanner = null;
    },

    dismissStuckAssignmentsBanner() {
      this.stuckAssignments = [];
    },

    populateSaveErrorBanner(fallbackTitle, backendError) {
      const raw = backendError && backendError.message;
      const msgs = Array.isArray(raw) ? raw : (raw ? [String(raw)] : []);
      const joined = msgs.join(' ').toLowerCase();

      const startStr = localDateToYyyyMmDd(this.schedule.start_date);
      const endStr = localDateToYyyyMmDd(this.schedule.end_date);

      let outOfRange = [];
      if (startStr && endStr && (joined.includes('outside') || joined.includes('date range') || joined.includes('every active assignment') || joined.includes('no longer covers'))) {
        outOfRange = (this.pendingAssignments || [])
          .filter(a => a && a.status !== 'Cancelled')
          .filter(a => {
            const dayStr = epochToYyyyMmDd(a.scheduled_date);
            return dayStr && !isDayInRange(dayStr, startStr, endStr);
          })
          .map(a => {
            const dayStr = epochToYyyyMmDd(a.scheduled_date) || '';
            const m = dayStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            const date_label = m ? `${m[3]}/${m[2]}/${m[1]}` : dayStr;
            return {
              engineer_name: a.engineer_name || a.engineer_id,
              date_label,
            };
          });
      }

      // Equipment double-booking: the server sends structured conflict rows
      // (serial / product / job) — render those as a table instead of raw text.
      const equipmentConflicts = Array.isArray(backendError && backendError.conflicts)
        ? backendError.conflicts
        : [];
      if (equipmentConflicts.length) {
        this.saveErrorBanner = {
          title: "Equipment is already booked on an active job — remove it from that job or close the job first.",
          out_of_range: [],
          equipment_conflicts: equipmentConflicts,
        };
        return;
      }

      const title = msgs.length
        ? msgs.map(m => this.formatValidationMessage(m)).join(' ')
        : fallbackTitle;

      this.saveErrorBanner = {
        title,
        out_of_range: outOfRange,
      };
    },

    scheduleDateChanged() {
      if (!this.originalScheduleDates) return true;
      const currentStart = localDateToYyyyMmDd(this.schedule.start_date);
      const currentEnd = localDateToYyyyMmDd(this.schedule.end_date);
      return (
        currentStart !== this.originalScheduleDates.start_yyyy_mm_dd ||
        currentEnd !== this.originalScheduleDates.end_yyyy_mm_dd
      );
    },

    computeStuckAssignments() {
      const startStr = localDateToYyyyMmDd(this.schedule.start_date);
      const endStr = localDateToYyyyMmDd(this.schedule.end_date);
      if (!startStr || !endStr) return [];
      const stuck = [];
      for (const a of (this.pendingAssignments || [])) {
        if (!a || a.status === 'Cancelled') continue;
        if (!a.assignment_id) continue; // ghost; not "stuck saved row"
        const dayStr = epochToYyyyMmDd(a.scheduled_date);
        if (!dayStr) continue;
        if (!isDayInRange(dayStr, startStr, endStr)) {
          const m = dayStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          stuck.push({
            assignment_id: a.assignment_id,
            engineer_id: a.engineer_id,
            engineer_name: a.engineer_name || a.engineer_id,
            date_label: m ? `${m[3]}/${m[2]}/${m[1]}` : dayStr,
          });
        }
      }
      return stuck;
    },

    async refreshAssignmentsFromServer() {
      if (!this.schedule_id) return;
      try {
        const assignmentData = await this.$store.dispatch("schedule/fetchAssignments", this.schedule_id);
        const assignments = assignmentData && assignmentData.data
            ? assignmentData.data
            : (this.$store.state.schedule.assignments || []);
        this.pendingAssignments = assignments
            .filter(a => a.status !== 'Cancelled')
            .map(a => ({
              engineer_id: a.engineer_id,
              engineer_name: a.engineer ? `${a.engineer.first_name || ''} ${a.engineer.last_name || ''}`.trim() : a.engineer_id,
              scheduled_date: a.scheduled_date,
              start_time: a.start_time || null,
              end_time: a.end_time || null,
              status: a.status || 'Assigned',
              assignment_id: a.assignment_id || null,
            }));
        this.initialAssignmentTimes = {};
        for (const a of this.pendingAssignments) {
          if (a.assignment_id) {
            this.initialAssignmentTimes[a.assignment_id] = {
              start_time: a.start_time || null,
              end_time: a.end_time || null,
            };
          }
        }
      } catch (err) {
        console.error('Error refreshing assignments after save:', err);
      }
    },

    revertCellToSnapshot(assignmentId) {
      if (!assignmentId) return;
      const snap = this.initialAssignmentTimes && this.initialAssignmentTimes[assignmentId];
      if (!snap) return;
      this.pendingAssignments = (this.pendingAssignments || []).map(a => {
        if (a && a.assignment_id === assignmentId) {
          return { ...a, start_time: snap.start_time || null, end_time: snap.end_time || null };
        }
        return a;
      });
    },

    formatScheduleTimeRange(start, end) {
      if (!start && !end) return "all-day";
      if (start && end) return `${start}–${end}`;
      if (start) return `from ${start}`;
      return `until ${end}`;
    },

    computeCascadeCandidates({ newStart, newEnd }) {
      // Rule (per user): every save makes engineer cells follow the current
      // schedule time. Any cell whose current time != schedule time is a
      // candidate, regardless of whether the schedule time changed this
      // session. Cells already matching are skipped (no-op).
      const ns = newStart || null;
      const ne = newEnd || null;
      const candidates = [];
      for (const a of (this.pendingAssignments || [])) {
        if (!a || a.status === 'Cancelled') continue;
        if (!a.assignment_id) continue;
        const cellStart = a.start_time || null;
        const cellEnd = a.end_time || null;
        if (cellStart === ns && cellEnd === ne) continue; // already at target
        candidates.push({
          assignment_id: a.assignment_id,
          engineer_id: a.engineer_id,
          engineer_name: a.engineer_name || a.engineer_id,
          scheduled_date: a.scheduled_date,
          current_start: cellStart,
          current_end: cellEnd,
        });
      }
      return candidates;
    },

    applyCascadeUpdate(candidates, { newStart, newEnd }) {
      if (!Array.isArray(candidates) || candidates.length === 0) return;
      const idSet = new Set(candidates.map(c => c.assignment_id));
      this.pendingAssignments = (this.pendingAssignments || []).map(a => {
        if (a && a.assignment_id && idSet.has(a.assignment_id)) {
          return { ...a, start_time: newStart || null, end_time: newEnd || null };
        }
        return a;
      });
    },

    // Tells the user what the date change did to the engineers' booked days:
    // moved with the job, booked on the added days, removed because the job
    // no longer covers them, or left unbooked because of a clash or leave.
    engineerNameOf(engineerId) {
      const known = (this.pendingAssignments || []).find(a => a && a.engineer_id === engineerId && a.engineer_name);
      if (known) return known.engineer_name;
      const board = this.$refs && this.$refs.dispatchBoard;
      const listed = board && Array.isArray(board.engineerList) ? board.engineerList.find(e => e.engineer_id === engineerId) : null;
      return listed ? listed.name : engineerId;
    },

    dayLabelOf(epoch) {
      const dayStr = epochToYyyyMmDd(epoch) || '';
      const m = dayStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return m ? `${m[3]}/${m[2]}/${m[1]}` : dayStr;
    },

    // A removed day the server refused stays booked. The reason goes in the
    // banner at the top of the page, where it is not missed.
    reportRemovalProblems(problems) {
      if (!problems || !problems.length) return;
      const lines = problems.map(p => `${this.engineerNameOf(p.engineer_id)} on ${this.dayLabelOf(p.scheduled_date)} — ${p.message}`);
      const current = this.dateChangeBanner || { lines: [], problems: [] };
      this.dateChangeBanner = { lines: current.lines, problems: [...current.problems, ...lines] };
    },

    reportDateChanges(changes) {
      if (!changes) return;

      const nameOf = (engineerId) => this.engineerNameOf(engineerId);
      const dayOf = (epoch) => this.dayLabelOf(epoch);
      const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

      const lines = [];
      if (changes.moved > 0) lines.push(`${plural(changes.moved, 'engineer day')} moved with the job dates.`);
      if (changes.added > 0) lines.push(`${plural(changes.added, 'engineer day')} booked on the added dates.`);
      const outside = (changes.dropped || []).filter(d => d.reason === 'outside');
      if (outside.length > 0) lines.push(`${plural(outside.length, 'engineer day')} removed as the job no longer covers ${outside.length === 1 ? 'it' : 'them'}.`);

      const reasonText = (reason) => reason === 'leave' ? 'on leave' : 'already booked on another job';
      const problems = [
        ...(changes.dropped || []).filter(d => d.reason === 'clash').map(d => `${nameOf(d.engineer_id)} on ${dayOf(d.scheduled_date)} — ${reasonText(d.reason)}, day removed`),
        ...(changes.skipped || []).map(s => `${nameOf(s.engineer_id)} on ${dayOf(s.scheduled_date)} — ${reasonText(s.reason)}, not booked`),
      ];

      if (!lines.length && !problems.length) return;
      this.dateChangeBanner = { lines, problems };
    },

    dismissDateChangeBanner() {
      this.dateChangeBanner = null;
    },

    // The bin icon on the dispatch board removes the engineer straight away
    // on a saved schedule, rather than waiting for the next Save.
    async onEngineerRemoved({ engineer_name, assignment_ids }) {
      const ids = (assignment_ids || []).filter(Boolean);
      if (!this.schedule_id || ids.length === 0) return;

      this.isBusy = true;
      const cancelled = [];
      let failed = 0;
      try {
        for (const assignmentId of ids) {
          try {
            await this.$store.dispatch("schedule/cancelAssignment", assignmentId);
            cancelled.push(assignmentId);
          } catch (err) {
            console.error("Error removing engineer day:", err);
            failed += 1;
          }
        }
        if (cancelled.length) {
          this.pruneCancelledLocalState(cancelled);
        }
        await this.refreshAssignmentsFromServer();
        this.stuckAssignments = this.computeStuckAssignments();

        if (failed === 0) {
          this.notifySuccess(`${engineer_name || 'Engineer'} removed from this schedule.`);
        } else {
          this.notifyError(`${failed} engineer day${failed === 1 ? '' : 's'} could not be removed. Please retry.`);
        }
      } finally {
        this.isBusy = false;
      }
    },

    pruneCancelledLocalState(cancelledIds) {
      if (!Array.isArray(cancelledIds) || cancelledIds.length === 0) return;
      const cancelledSet = new Set(cancelledIds);
      this.pendingAssignments = (this.pendingAssignments || []).filter(
        a => !(a && a.assignment_id && cancelledSet.has(a.assignment_id)),
      );
      if (this.initialAssignmentTimes) {
        for (const id of cancelledIds) {
          if (this.initialAssignmentTimes[id]) {
            delete this.initialAssignmentTimes[id];
          }
        }
      }
      if (this.$refs && this.$refs.dispatchBoard && typeof this.$refs.dispatchBoard.clearRemoved === "function") {
        this.$refs.dispatchBoard.clearRemoved(cancelledIds);
      }
    },

    async markStuckForRemoval(assignmentId) {
      if (!assignmentId) return;
      try {
        await this.$store.dispatch("schedule/cancelAssignment", assignmentId);
        this.pruneCancelledLocalState([assignmentId]);
        this.stuckAssignments = this.computeStuckAssignments();
        this.notifySuccess("Out-of-range engineer day removed.");
      } catch (err) {
        console.error("Error removing stuck assignment:", err);
        this.notifyError("Failed to remove the engineer day. Please retry.");
      }
    },

    buildScheduleUpdatePayload({ startTime, endTime }) {
      return {
        notes: this.schedule.notes || '',
        is_site_visit: this.schedule.is_site_visit || false,
        is_pre_pay: this.schedule.is_pre_pay || false,
        is_install: this.schedule.is_install || false,
        requires_parts_collection: this.schedule.requires_parts_collection || false,
        is_confirmed: this.schedule.is_confirmed || false,
        confirmed_by: this.schedule.confirmed_by || '',
        start_time: startTime || '',
        end_time: endTime || '',
        is_contractor: !!this.schedule.is_contractor,
        contact_id: this.schedule.is_contractor ? (this.schedule.contact_id || null) : null,
      };
    },

    applyContractorFromApi(data) {
      if (!data) return;
      this.schedule.is_contractor = !!data.is_contractor;
      this.schedule.contact_id = data.contact_id || null;
      const name = (data.contractor && data.contractor.name) || null;
      this.schedule.contractor_name = name;
      this.schedule.contractor = data.contact_id ? { contact_id: data.contact_id, name } : null;
    },

    setContractorContact(contact) {
      if (!contact) {
        this.schedule.contact_id = null;
        this.schedule.contractor_name = null;
        this.schedule.contractor = null;
        return;
      }
      this.schedule.contact_id = contact.contact_id || null;
      this.schedule.contractor_name = contact.name || null;
      this.schedule.contractor = contact;
    },

    handleContractorSelected(event) {
      const picked = event && event.value ? event.value : event;
      this.setContractorContact(picked);
    },

    handleContractorCleared() {
      this.setContractorContact(null);
    },

    apiErrorMessage(error, fallback) {
      const data = (error && error.data) || (error && error.response && error.response.data) || null;
      const message = data && data.message;
      if (Array.isArray(message) && message.length) return String(message[0]);
      if (typeof message === "string" && message) return message;
      return fallback;
    },

    notifyFinalSaveOutcome({ mode, thrown, hadAssignmentError, rejectedCount, createdCount, lastAssignmentError, removalFailed }) {
      const verb = mode === "edit" ? "updated" : "created";
      if (thrown) {
        this.notifyError(mode === "edit" ? "Couldn't update schedule" : "Couldn't create schedule");
        return;
      }
      if (removalFailed > 0) {
        const days = removalFailed === 1 ? "1 engineer day" : `${removalFailed} engineer days`;
        this.notifyWarning(`Schedule ${verb}, but ${days} could not be removed. See the note at the top of the page.`);
        return;
      }
      if (hadAssignmentError) {
        const tail = lastAssignmentError ? ` (${lastAssignmentError})` : "";
        this.notifyError(`Schedule ${verb} but an engineer time change was rejected${tail}. The cell has been reverted.`);
        return;
      }
      if (rejectedCount > 0) {
        const added = typeof createdCount === "number" ? createdCount : 0;
        this.notifyWarning(`Schedule ${verb}. ${added} added, ${rejectedCount} rejected (clashes).`);
        return;
      }
      this.notifySuccess(`Schedule ${verb} successfully`);
    },

    resetLocalScheduleForm() {
      this.schedule = {
        customer: null,
        start_date: null,
        end_date: null,
        start_time: null,
        end_time: null,
        notes: '',
        is_confirmed: false,
        confirmed_by: '',
        is_site_visit: false,
        is_pre_pay: false,
        is_install: false,
        requires_parts_collection: false,
        is_contractor: false,
        contact_id: null,
        contractor_name: null,
        contractor: null,
      };
      this.customer = {};
      this.readOnly = false;
      this.pendingAssignments = [];
      this.initialAssignmentTimes = {};
      this.selectedEngineer = null;
      this.assignmentResult = null;
      this.validationErrors = {};
      this.saveErrorBanner = null;
      this.originalScheduleDates = null;
      this.originalScheduleTimes = null;
      this.stuckAssignments = [];
      this.isBusy = false;
    },

    async enterRouteMode(route) {
      const routeScheduleId = route && route.params && route.params.schedule_id;
      if (routeScheduleId) {
        await this.loadSchedule(routeScheduleId);
        return;
      }
      this.resetLocalScheduleForm();
      await this.$store.dispatch("scheduleManage/clearAll");
      const customer_id = (route && route.query && route.query.customer_id) || this.customer_id;
      const equipment_id = (route && route.query && route.query.equipment_id) || this.equipment_id;
      if (customer_id) {
        await this.fetchAndUpdateContactDetails(customer_id);
        await this.$store.dispatch("scheduleManage/updateCurrentCustomerId", customer_id);
        const q = (route && route.query) || {};
        if (q.fromTab) {
          await this.$store.dispatch("scheduleManage/applyRouteContext", {
            sourceTab: q.fromTab,
            fromDate: q.fromDate || null,
            toDate: q.toDate || null,
          });
        }
        if (equipment_id) {
          await this.prefillEquipment(equipment_id);
        }
      }
    },

    async prefillEquipment(equipmentId) {
      const items = this.$store.state.scheduleManage.equipment || [];
      const target = findPrefillTarget(items, equipmentId, this.currentScheduleId || null);
      if (!target) {
        this.notifyWarning("Equipment not found in this customer's list.");
        return;
      }
      await this.$store.dispatch("scheduleManage/setFilter", { key: "bucket", value: target.bucket });
      this.highlightEquipmentId = target.equipment_id;
    },

    handleContactCleared(selectedContact) {
      if (!selectedContact || !selectedContact.value || !selectedContact.value.contact_id) {
        this.customer = {};
        this.$store.dispatch("scheduleManage/clearAll");
        return;
      }
    },

    async fetchAndUpdateContactDetails(contact_id) {
      try {
        const response = await this.ContactService.searchContactsWithType({contact_id});
        if (response && response.length > 0) {
          this.updateCustomerDetails(response[0]);
          if (!this.schedule.customer) {
            this.schedule.customer = response[0];
          }
        } else {
          console.error('No contact details found for the given ID');
        }
      } catch (error) {
        console.error('Error fetching contact details:', error);
      }
    },

    handleContactSelected: function (selectedCustomer) {
      if (!selectedCustomer || !selectedCustomer.value || !selectedCustomer.value.contact_id) {
        this.customer = {};
        this.$store.dispatch("scheduleManage/clearAll");
        return;
      }

      this.updateCustomerDetails(selectedCustomer.value);
      this.$store.dispatch("scheduleManage/updateCurrentCustomerId", selectedCustomer.value.contact_id);
    },

    updateCustomerDetails: function (selectedCustomer) {
      this.customer.address = this.formatDefaultAddress(selectedCustomer);
      this.customer.people = selectedCustomer.people;
      this.customer.vts_number = selectedCustomer.vts_number || null;
      this.loadComments(selectedCustomer.contact_id);
    },

    formatDefaultAddress: function (selectedCustomer) {
      if (!selectedCustomer || !selectedCustomer.address) {
        return ``;
      }

      let address = selectedCustomer.address;

      return [
        address.address1,
        address.address2,
        address.address3,
        address.city,
        address.postcode,
      ]
          .filter((part) => !!part)
          .join(", ");
    },

    loadComments: function (contact_id) {
      try {
        this.CommentService.getCommentByResourceIdAndTypeId(contact_id, "contact")
            .then(result => {
              const comments = this.getFirstCommentContent(result.data);
              this.$set(this.customer, 'comments', comments);
            })
            .catch(error => {
              console.error("Error fetching comments:", error);
            });
      } catch (error) {
        console.error(error);
      }
    },

    getFirstCommentContent: function (comments) {
      if (comments && comments.length > 0) {
        return comments[0].content;
      }
      return "No Notes";
    },

    async loadSchedule(scheduleId) {
      try {
        this.isBusy = true;
        const data = await this.$store.dispatch("schedule/fetchScheduleById", scheduleId);
        if (data) {
          this.schedule = {
            ...data,
            customer: data.customer || null,
            start_date: epochToLocalDateAtMidnight(data.start_date),
            end_date: epochToLocalDateAtMidnight(data.end_date),
            start_time: data.start_time ? this.parseTimeString(data.start_time) : null,
            end_time: data.end_time ? this.parseTimeString(data.end_time) : null,
            notes: data.notes || '',
            confirmed_by: data.confirmed_by || '',
            is_confirmed: data.is_confirmed || false,
            is_site_visit: data.is_site_visit || false,
            is_pre_pay: data.is_pre_pay || false,
            is_install: data.is_install || false,
            requires_parts_collection: data.requires_parts_collection || false,
          };
          this.originalScheduleDates = {
            start_yyyy_mm_dd: localDateToYyyyMmDd(this.schedule.start_date),
            end_yyyy_mm_dd: localDateToYyyyMmDd(this.schedule.end_date),
          };
          this.originalScheduleTimes = {
            start_time: data.start_time || null,
            end_time: data.end_time || null,
          };
          this.applyContractorFromApi(data);

          if (data.customer_id) {
            await this.fetchAndUpdateContactDetails(data.customer_id);
          }

          try {
            const assignmentData = await this.$store.dispatch("schedule/fetchAssignments", scheduleId);
            const assignments = assignmentData && assignmentData.data ? assignmentData.data : (this.$store.state.schedule.assignments || []);
            this.pendingAssignments = assignments
              .filter(a => a.status !== 'Cancelled')
              .map(a => ({
                engineer_id: a.engineer_id,
                engineer_name: a.engineer ? `${a.engineer.first_name || ''} ${a.engineer.last_name || ''}`.trim() : a.engineer_id,
                scheduled_date: a.scheduled_date,
                start_time: a.start_time || null,
                end_time: a.end_time || null,
                status: a.status || 'Assigned',
                assignment_id: a.assignment_id || null,
              }));

            this.initialAssignmentTimes = {};
            for (const a of this.pendingAssignments) {
              if (a.assignment_id) {
                this.initialAssignmentTimes[a.assignment_id] = {
                  start_time: a.start_time || null,
                  end_time: a.end_time || null,
                };
              }
            }

            this.stuckAssignments = this.computeStuckAssignments();
          } catch (err) {
            console.error("Error loading assignments:", err);
          }

          if (data.schedule_id) {
            await this.$store.dispatch("scheduleManage/updateCurrentScheduleId", data.schedule_id);
          }
          if (data.customer_id) {
            await this.$store.dispatch("scheduleManage/updateCurrentCustomerId", data.customer_id);
          }
        }
      } catch (error) {
        console.error("Error loading schedule:", error);
      } finally {
        this.isBusy = false;
      }
    },

    parseTimeString(timeStr) {
      if (!timeStr) return null;
      const parts = timeStr.split(':');
      if (parts.length < 2) return null;
      const d = new Date();
      d.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
      return d;
    },

    formatDate(dateObj) {
      if (!dateObj) return '';
      return this.$moment(dateObj).format('DD/MM/YYYY');
    },
    formatEpoch(epoch) {
      if (!epoch) return '';
      return this.$moment.unix(epoch).format('DD/MM/YYYY');
    },

    formatRejectionReason,
    dismissAssignmentResult() {
      this.assignmentResult = null;
    },

    onPlannerFilterChange(payload) {
      this.$store.dispatch("scheduleManage/setFilter", payload);
    },
    // Enter saves the form from any field except a notes box, where it
    // starts a new line as people expect.
    onEnterKey(event) {
      const target = event && event.target;
      if (target && String(target.tagName).toUpperCase() === 'TEXTAREA') {
        return;
      }
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      this.saveSchedule();
    },

    onPlannerToggleCategory(payload) {
      if (this.plannerReadOnly) {
        this.notifyWarning(`This schedule is ${this.schedule.status} and can no longer be changed.`);
        return;
      }
      if (!payload.value && this.isLastWorkItemOnSchedule(payload)) {
        this.notifyWarning("This is the last item on the job. A saved job must keep at least one equipment item — cancel the job instead.");
        return;
      }
      this.$store.dispatch("scheduleManage/toggleCategory", payload);
    },
    isLastWorkItemOnSchedule({ equipment_id, category }) {
      if (!this.schedule_id) return false;
      const isTopLevelJob = !!(this.schedule.is_site_visit || this.schedule.is_install);
      if (isTopLevelJob) return false;

      const effective = this.effectiveEquipmentPayload();
      if (effective.length !== 1) return false;

      const only = effective[0];
      if (only.equipment_id !== equipment_id) return false;

      const flagsOn = ['service', 'calibration', 'warranty', 'repair', 'install']
          .filter(cat => only[`is_${cat}`]);
      return flagsOn.length === 1 && flagsOn[0] === category;
    },
    onPlannerNavigateLocked({ schedule_id }) {
      if (!schedule_id) return;
      this.$router.push({ name: "schedule-edit", params: { schedule_id } });
    },
    onPlannerOpenSchedule({ schedule_id }) {
      if (!schedule_id) return;
      this.$router.push({ name: "schedule-edit", params: { schedule_id } });
    },

    validateSchedule() {
      if (!this.schedule.customer || !this.schedule.customer.contact_id) {
        this.notifyError("Please select a customer.");
        return false;
      }

      if (!this.schedule.start_date || !this.$moment(this.schedule.start_date).isValid()) {
        this.notifyError("Please select a valid start date.");
        return false;
      }

      if (!this.schedule.end_date || !this.$moment(this.schedule.end_date).isValid()) {
        this.notifyError("Please select a valid end date.");
        return false;
      }

      if (this.$moment(this.schedule.end_date).isBefore(this.$moment(this.schedule.start_date))) {
        this.notifyError("End date must be on or after start date.");
        return false;
      }

      const isTopLevelJob = !!(this.schedule.is_site_visit || this.schedule.is_install);
      const effective = this.effectiveEquipmentPayload();
      if (!effective.length && !isTopLevelJob) {
        this.notifyError("Please select at least one equipment with a job type (Service, Calibration, Warranty, Repair, or Install), or enable Site Visit / Install at the top.");
        return false;
      }

      return true;
    },

    effectiveEquipmentPayload() {
      const currentScheduleId = this.$store.state.scheduleManage.schedule_id;
      const rows = this.$store.state.scheduleManage.equipment || [];

      return rows
          .map(item => {
            const eq = item && item.equipment;
            const sd = item && item.scheduleDetail;
            if (!sd || !sd.equipment_id) return null;

            const free = cat => !isCategoryLockedElsewhere(eq, cat, currentScheduleId);

            const entry = {
              equipment_id: sd.equipment_id,
              is_service:     !!sd.is_service     && free('service'),
              is_calibration: !!sd.is_calibration && free('calibration'),
              is_warranty:    !!sd.is_warranty    && free('warranty'),
              is_repair:      !!sd.is_repair      && free('repair'),
              is_install:     !!sd.is_install     && free('install'),
            };
            const hasAny =
                entry.is_service || entry.is_calibration ||
                entry.is_warranty || entry.is_repair || entry.is_install;
            return hasAny ? entry : null;
          })
          .filter(Boolean);
    },

    formatValidationMessage(message) {
      if (!message || typeof message !== 'string') return '';
      message = message.replace(/["']/g, '');
      message = message.replace(/\s+/g, ' ').trim();
      // snake_case-ish identifier (no spaces, has underscore) → Title Case it
      const looksLikeIdentifier = message.indexOf(' ') === -1 && message.indexOf('_') !== -1;
      if (looksLikeIdentifier) {
        message = message.replace(/_/g, ' ');
        return message.replace(/\b\w/g, c => c.toUpperCase());
      }
      // Otherwise treat as a sentence: only upper-case first char, preserve the rest
      return message.charAt(0).toUpperCase() + message.slice(1);
    },

    saveSchedule: async function () {
      try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch (e) { /* ignore */ }

      if (!this.validateSchedule()) return;

      const startTime = this.schedule.start_time
          ? this.$moment(this.schedule.start_time).format('HH:mm')
          : null;
      const endTime = this.schedule.end_time
          ? this.$moment(this.schedule.end_time).format('HH:mm')
          : null;

      if (this.schedule_id) {
        const cascadeCandidates = this.computeCascadeCandidates({ newStart: startTime, newEnd: endTime });
        if (cascadeCandidates.length > 0) {
          let preflight = null;
          try {
            preflight = await ScheduleService.checkClashes({
              exclude_schedule_id: this.schedule_id,
              assignments: cascadeCandidates.map(c => ({
                engineer_id: c.engineer_id,
                scheduled_date: c.scheduled_date,
                start_time: startTime,
                end_time: endTime,
              })),
            });
          } catch (preflightErr) {
            console.error('Cascade preflight clash check failed:', preflightErr);
          }
          const clashes = (preflight && Array.isArray(preflight.clashes)) ? preflight.clashes : [];
          if (clashes.length > 0) {
            const engineerNameById = (id) => {
              const found = cascadeCandidates.find(c => c.engineer_id === id);
              return found ? found.engineer_name : id;
            };
            const bannerClashes = clashes.map(c => {
              const name = engineerNameById(c.engineer_id);
              let detail;
              if (c.type === 'invalid-time-range') detail = 'invalid time range';
              else if (c.type === 'approved-leave') detail = 'on approved leave';
              else detail = c.message || 'clashes with another schedule';
              const date_label = c.scheduled_date ? this.$moment.unix(c.scheduled_date).format('DD/MM/YYYY') : null;
              return { engineer_name: name, detail, date_label };
            });
            this.saveErrorBanner = {
              title: "Cannot apply the new schedule time — some engineer bookings would clash with another schedule. Update or remove the conflicting bookings first.",
              clashes: bannerClashes,
              out_of_range: [],
            };
            return;
          }
          this.applyCascadeUpdate(cascadeCandidates, { newStart: startTime, newEnd: endTime });
        }
      }

      this.isBusy = true;
      this.assignmentResult = null;
      this.saveErrorBanner = null;
      this.dateChangeBanner = null;

      try {
        const startEpoch = dateToUtcMidnightEpoch(this.schedule.start_date);
        const endEpoch = dateToUtcMidnightEpoch(this.schedule.end_date);

        const equipmentPayload = this.effectiveEquipmentPayload();

        const payload = {
          customer_id: this.schedule.customer.contact_id,
          start_date: startEpoch,
          end_date: endEpoch,
          start_time: startTime || '',
          end_time: endTime || '',
          equipment_list: equipmentPayload,
          notes: this.schedule.notes || '',
          is_site_visit: this.schedule.is_site_visit || false,
          is_pre_pay: this.schedule.is_pre_pay || false,
          is_install: this.schedule.is_install || false,
          requires_parts_collection: this.schedule.requires_parts_collection || false,
          is_contractor: !!this.schedule.is_contractor,
          contact_id: this.schedule.is_contractor ? (this.schedule.contact_id || null) : null,
        };

        const activePending = this.$refs.dispatchBoard
            ? this.$refs.dispatchBoard.getActivePendingAssignments()
            : this.pendingAssignments;
        let newAssignments = activePending
            .filter(a => !a.assignment_id)
            .map(a => ({
              engineer_id: a.engineer_id,
              scheduled_date: a.scheduled_date,
              start_time: a.start_time || null,
              end_time: a.end_time || null,
            }));
        if (newAssignments.length > 0) {
          payload.assignments = newAssignments;
        }

        if (this.schedule_id) {
          let createdCount = 0;
          let rejectedCount = 0;
          let hadAssignmentError = false;
          let lastAssignmentError = null;

          const updateSchedulePayload = this.buildScheduleUpdatePayload({ startTime, endTime });
          await this.$store.dispatch("schedule/updateSchedule", {
            scheduleId: this.schedule_id,
            payload: updateSchedulePayload,
          });
          this.originalScheduleTimes = {
            start_time: startTime || null,
            end_time: endTime || null,
          };

          const isTopLevelJob = !!(this.schedule.is_site_visit || this.schedule.is_install);
          if (!this.inProgressLock && (equipmentPayload.length > 0 || isTopLevelJob)) {
            try {
              await this.$store.dispatch("schedule/updateScheduleEquipment", {
                scheduleId: this.schedule_id,
                payload: { equipment_list: equipmentPayload },
              });
            } catch (equipmentErr) {
              // The list on screen no longer matches what is saved, so put
              // it back to the server state before surfacing the error.
              await this.$store.dispatch("scheduleManage/fetchEquipmentForCustomer");
              throw equipmentErr;
            }
          }

          const earlyRemovedList = this.$refs.dispatchBoard ? this.$refs.dispatchBoard.getRemovedAssignments() : [];
          const successfullyCancelledIds = [];
          const removalProblems = [];
          for (const removed of earlyRemovedList) {
            if (removed.assignment_id) {
              try {
                await this.$store.dispatch("schedule/cancelAssignment", removed.assignment_id);
                successfullyCancelledIds.push(removed.assignment_id);
              } catch (cancelErr) {
                console.error("Error cancelling assignment:", cancelErr);
                removalProblems.push({
                  assignment_id: removed.assignment_id,
                  engineer_id: removed.engineer_id,
                  scheduled_date: removed.scheduled_date,
                  message: this.apiErrorMessage(cancelErr, "the day could not be removed"),
                });
              }
            }
          }
          if (successfullyCancelledIds.length) {
            this.pruneCancelledLocalState(successfullyCancelledIds);
          }
          if (removalProblems.length && this.$refs.dispatchBoard) {
            // Put the refused days back to booked on the board.
            this.$refs.dispatchBoard.clearRemoved(removalProblems.map(p => p.assignment_id));
          }

          // Times first, while the rows on screen still match the server:
          // any cell whose time changed (by hand or by the cascade) is saved.
          const removedKeys = new Set(earlyRemovedList.map(r => `${r.engineer_id}_${r.scheduled_date}`));
          const timeEditedExisting = (this.pendingAssignments || []).filter(a => {
            if (!a || !a.assignment_id) return false;
            if (removedKeys.has(`${a.engineer_id}_${a.scheduled_date}`)) return false;
            const snap = this.initialAssignmentTimes[a.assignment_id];
            if (!snap) return false;
            const curStart = a.start_time || null;
            const curEnd = a.end_time || null;
            return curStart !== snap.start_time || curEnd !== snap.end_time;
          });
          for (const a of timeEditedExisting) {
            try {
              await this.$store.dispatch("schedule/updateAssignment", {
                assignmentId: a.assignment_id,
                payload: { start_time: a.start_time || null, end_time: a.end_time || null },
              });
              this.initialAssignmentTimes[a.assignment_id] = {
                start_time: a.start_time || null,
                end_time: a.end_time || null,
              };
            } catch (timeErr) {
              const errMsg = (timeErr && (timeErr.message || (timeErr.data && timeErr.data.message))) || String(timeErr);
              console.error("Error updating assignment time:", timeErr);
              hadAssignmentError = true;
              if (/already has an assignment/i.test(errMsg)) {
                lastAssignmentError = `${a.engineer_name || a.engineer_id} already has a clashing assignment`;
              } else {
                lastAssignmentError = errMsg;
              }
              this.revertCellToSnapshot(a.assignment_id);
            }
          }

          // Then the dates. The server moves, adds or removes the engineers'
          // booked days to match and tells us what it did.
          let dateChanges = null;
          if (this.scheduleDateChanged()) {
            const dateResult = await this.$store.dispatch("schedule/updateScheduleDates", {
              scheduleId: this.schedule_id,
              payload: { start_date: startEpoch, end_date: endEpoch, rebase_assignments: true },
            });
            dateChanges = dateResult && dateResult.assignment_changes ? dateResult.assignment_changes : null;
            this.originalScheduleDates = {
              start_yyyy_mm_dd: localDateToYyyyMmDd(this.schedule.start_date),
              end_yyyy_mm_dd: localDateToYyyyMmDd(this.schedule.end_date),
            };
            await this.refreshAssignmentsFromServer();

            // A cell ticked on a new day may already have been booked by the
            // date change; do not book it twice.
            const nowBooked = new Set((this.pendingAssignments || []).map(a => `${a.engineer_id}_${a.scheduled_date}`));
            newAssignments = newAssignments.filter(a => !nowBooked.has(`${a.engineer_id}_${a.scheduled_date}`));
          }

          if (newAssignments.length > 0) {
            try {
              const assignResult = await this.$store.dispatch("schedule/addAssignments", {
                scheduleId: this.schedule_id,
                payload: { assignments: newAssignments },
              });
              createdCount = (assignResult && assignResult.created && assignResult.created.length) || 0;
              rejectedCount = (assignResult && assignResult.rejected && assignResult.rejected.length) || 0;
              if (rejectedCount > 0) {
                this.assignmentResult = buildAssignmentResult(this.schedule_id, assignResult, this.pendingAssignments);
              }
            } catch (assignErr) {
              console.error("Error adding assignments:", assignErr);
              hadAssignmentError = true;
            }
          }

          await this.refreshAssignmentsFromServer();

          this.notifyFinalSaveOutcome({ mode: "edit", thrown: false, hadAssignmentError, rejectedCount, createdCount, lastAssignmentError, removalFailed: removalProblems.length });
          this.reportDateChanges(dateChanges);
          this.reportRemovalProblems(removalProblems);
          this.stuckAssignments = this.computeStuckAssignments();
        } else {
          const result = await this.$store.dispatch("schedule/createSchedule", payload);

          const newScheduleId = result.schedule && result.schedule.schedule_id;
          const createdCount = (result.assignments && result.assignments.created && result.assignments.created.length) || 0;
          const rejectedCount = (result.assignments && result.assignments.rejected && result.assignments.rejected.length) || 0;

          if (result.assignments) {
            this.assignmentResult = buildAssignmentResult(newScheduleId, result.assignments, this.pendingAssignments);
          }

          this.notifyFinalSaveOutcome({ mode: "create", thrown: false, hadAssignmentError: false, rejectedCount, createdCount });

          await this.$router.push({
            name: "schedule-edit",
            params: { schedule_id: newScheduleId },
          });
        }
      } catch (error) {
        var errorMessage = this.schedule_id
            ? "Couldn't update schedule"
            : "Couldn't create schedule";

        var backendError = (error && error.response && error.response.data)
            ? error.response.data
            : (error && error.data)
                ? error.data
                : null;

        this.populateSaveErrorBanner(errorMessage, backendError);

        function normalizeMessages(be) {
          if (!be) return [];

          if (be.message) {
            if (Object.prototype.toString.call(be.message) === '[object Array]') {
              var arr = [];
              for (var i = 0; i < be.message.length; i++) {
                if (be.message[i]) arr.push(String(be.message[i]));
              }
              if (arr.length) return arr;
            }
            if (typeof be.message === 'string' && be.message.replace(/\s+/g, '').length) {
              return [be.message];
            }
          }

          if (be.errors && Object.prototype.toString.call(be.errors) === '[object Array]') {
            var msgs = [];
            for (var j = 0; j < be.errors.length; j++) {
              var e = be.errors[j];
              if (e && e.constraints && typeof e.constraints === 'object') {
                for (var k in e.constraints) {
                  if (e.constraints.hasOwnProperty(k)) msgs.push(String(e.constraints[k]));
                }
              }
              if (e && e.children && Object.prototype.toString.call(e.children) === '[object Array]') {
                for (var c = 0; c < e.children.length; c++) {
                  var child = e.children[c];
                  if (child && child.constraints && typeof child.constraints === 'object') {
                    for (var ck in child.constraints) {
                      if (child.constraints.hasOwnProperty(ck)) msgs.push(String(child.constraints[ck]));
                    }
                  }
                }
              }
            }
            if (msgs.length) return msgs;
          }

          if (be.constraints && typeof be.constraints === 'object') {
            var list = [];
            for (var key in be.constraints) {
              if (be.constraints.hasOwnProperty(key)) list.push(String(be.constraints[key]));
            }
            if (list.length) return list;
          }

          if (typeof be === 'string' && be.replace(/\s+/g, '').length) {
            return [be];
          }

          return [];
        }

        var messages = normalizeMessages(backendError);

        if (messages.length) {
          this.validationErrors = messages;
          // Banner shows the message; no extra toast to avoid duplicates.
        } else {
          this.validationErrors = [];
          this.notifyFinalSaveOutcome({
            mode: this.schedule_id ? "edit" : "create",
            thrown: true,
            hadAssignmentError: false,
            rejectedCount: 0,
            createdCount: 0,
          });
        }

        if (backendError) {
          console.error(backendError.message ? backendError.message : backendError);
        } else {
          console.error(errorMessage);
        }

      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>
